/** The minimap's data for the selected mission: the ambulance's GPS positions (its device's status packets,
 *  every ~10-12 s), its receiving facility and the road left to it.
 *
 *  Read every few seconds while the mission is under way, asking only for positions newer than the last
 *  one; once it is over (arrived, handed over or ended) only now and then, for a late facility change. */

import { useCallback, useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";

import { fetchTrack } from "./api";
import type { Track, TrackPoint } from "./types";

const LIVE_POLL_MS = 3000;
const OVER_POLL_MS = 30000;

export type MissionTrack = {
  positions: TrackPoint[];
  facility: Track["facility"];
  route: Track["route"];
  /** With no facility selected: the facilities nearest by drive time; `route` goes to the first. */
  nearest: Track["nearest"];
  journeyOver: boolean;
  /** When the device last reported a GPS fix: how old the GPS is, also while parked. */
  fixAt: string | null;
  /** When the newest position came in, on this page's clock (for the marker's glide). */
  receivedAt: number | null;
};

const EMPTY: MissionTrack = {
  positions: [],
  facility: null,
  route: null,
  nearest: [],
  journeyOver: false,
  fixAt: null,
  receivedAt: null,
};

export function useMissionTrack(missionId: string | null, ended: boolean): MissionTrack {
  const [cookies] = useCookies(["access_token"]);
  const token: string | undefined = cookies.access_token;
  const [track, setTrack] = useState<MissionTrack>(EMPTY);
  const trackRef = useRef(track);
  trackRef.current = track;
  const missionRef = useRef(missionId);
  missionRef.current = missionId;

  const load = useCallback(async () => {
    if (!token || !missionId) return;
    const known = trackRef.current.positions;
    const since = known.length ? known[known.length - 1].at : undefined;
    try {
      const next = await fetchTrack(token, missionId, since);
      if (missionRef.current !== missionId) return; // another mission was picked meanwhile
      setTrack((current) => {
        const fresh = next.positions.filter((p) => !since || p.at > since);
        return {
          positions: fresh.length ? [...current.positions, ...fresh] : current.positions,
          facility: next.facility,
          route: next.route,
          nearest: next.nearest ?? [],
          journeyOver: next.journey_over,
          // A poll with nothing new says no fix_at: keep the last one.
          fixAt: next.fix_at && (!current.fixAt || next.fix_at > current.fixAt) ? next.fix_at : current.fixAt,
          receivedAt: fresh.length ? Date.now() : current.receivedAt,
        };
      });
    } catch {
      // The map keeps what it has; the next read tries again.
    }
  }, [token, missionId]);

  useEffect(() => {
    setTrack(EMPTY);
    trackRef.current = EMPTY;
    if (!missionId) return undefined;
    void load();
    return undefined;
  }, [missionId, load]);

  const over = ended || track.journeyOver;
  useEffect(() => {
    if (!missionId) return undefined;
    const timer = setInterval(() => void load(), over ? OVER_POLL_MS : LIVE_POLL_MS);
    return () => clearInterval(timer);
  }, [missionId, load, over]);

  return track;
}
